import { useState } from "react";
import { motion } from "framer-motion";
import { Link2, ShieldCheck, MessageCircle, FileText, Wallet, PackageCheck, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../Reveal";
import { useLang } from "../../i18n/LanguageContext";
import { ASSETS, waLink } from "../../lib/site";

const benefitIcons = [MessageCircle, FileText, Wallet, PackageCheck];
const MARKETPLACES = ["1688", "Alibaba", "Taobao"];

export const SendLink = () => {
  const { t } = useLang();
  const [url, setUrl] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const value = url.trim();
    if (!value) {
      toast.error(t.sendLink.empty);
      return;
    }
    window.open(waLink(t.sendLink.waMsg + value), "_blank");
    setUrl("");
  };

  return (
    <section data-testid="send-link-section" className="bg-white py-20 md:py-28 border-t border-black/[0.05]">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* LEFT */}
        <div>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sinovar-red/10 text-sinovar-red">
            <Link2 className="h-6 w-6" strokeWidth={1.8} />
          </div>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display text-4xl md:text-[3.2rem] font-bold tracking-tight text-sinovar-navy leading-[1.03]">
              {t.sendLink.title}
              <br />
              <span className="text-sinovar-red">{t.sendLink.highlight}</span>
            </h2>
          </Reveal>
          <p className="mt-6 max-w-lg text-lg text-sinovar-navy/65 leading-relaxed">{t.sendLink.desc}</p>

          <form onSubmit={submit} className="mt-8 rounded-2xl border border-black/[0.08] bg-white p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                inputMode="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t.sendLink.placeholder}
                data-testid="send-link-input"
                className="w-full flex-1 rounded-xl border border-black/10 bg-white px-5 py-3.5 text-base text-sinovar-navy placeholder:text-sinovar-navy/40 outline-none focus:border-sinovar-blue transition-colors"
              />
              <button
                type="submit"
                data-testid="send-link-submit"
                className="shrink-0 rounded-xl bg-sinovar-blue px-8 py-3.5 text-[15px] font-semibold text-white hover:bg-[#0b3c8a] transition-colors"
              >
                {t.sendLink.cta}
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2 px-1 text-sm text-sinovar-navy/45">
              <ShieldCheck className="h-4 w-4 text-sinovar-blue" />
              {t.sendLink.privacy}
            </div>
          </form>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
            {t.sendLink.benefits.map((b, i) => {
              const Icon = benefitIcons[i];
              return (
                <div key={i} className="flex items-start gap-2.5" data-testid={`sendlink-benefit-${i}`}>
                  <Icon className="h-6 w-6 shrink-0 text-sinovar-blue" strokeWidth={1.6} />
                  <span className="text-sm font-medium text-sinovar-navy/75 leading-tight">{b}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT */}
        <div className="relative">
          <div className="relative rounded-3xl bg-white p-2 md:p-4">
            <img src={ASSETS.sendlinkProducts} alt="Products sourced from China" className="mx-auto w-full max-w-md rounded-2xl object-contain" />
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-56 rounded-2xl bg-white p-5 shadow-xl border border-black/5"
            >
              <ul className="space-y-3">
                {t.sendLink.checklist.map((c, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm font-medium text-sinovar-navy">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-sinovar-blue" />
                    {c}
                  </li>
                ))}
              </ul>
            </motion.div>
            <MapPin className="absolute right-8 top-6 h-8 w-8 text-sinovar-red" fill="#E53935" />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-sinovar-navy">{t.sendLink.popular}</span>
            {MARKETPLACES.map((m) => (
              <span key={m} className="rounded-lg border border-black/10 bg-white px-3.5 py-1.5 text-sm font-medium text-sinovar-navy/70">
                {m}
              </span>
            ))}
            <span className="text-sm text-sinovar-navy/40">{t.common.comingSoon === "Coming soon" ? "and more" : "dan lainnya"}</span>
          </div>
        </div>
      </div>
    </section>
  );
};
